import React, { useRef } from "react";
import { Plus, X, Package } from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, auth } from "../firebase";

const MAX_PRODUCTS = 10;

const cx = (...list) => list.filter(Boolean).join(" ");

const emptyProduct = () => ({
  _key: Math.random().toString(36).slice(2),
  ad: "",
  aciklama: "",
  imageUrl: "",
  imageFile: null,
  imagePreviewUrl: "",
});


export async function uploadAndPrepareProducts(products) {
  const user = auth.currentUser;
  const uid = user?.uid || "admin";

  const results = await Promise.all(
    products.map(async (p) => {
      let finalUrl = p.imageUrl;

      if (p.imageFile) {
        const filename = `urun_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2)}_${p.imageFile.name}`;
        const storageRef = ref(storage, `firmalar/${uid}/urunler/${filename}`);
        await uploadBytes(storageRef, p.imageFile);
        finalUrl = await getDownloadURL(storageRef);
      }

      return {
        ad: p.ad.trim(),
        aciklama: p.aciklama.trim(),
        imageUrl: finalUrl,
      };
    })
  );

  return results.filter((p) => p.ad); // boş isimlileri atla
}

function ProductCard({ product, index, onChange, onRemove }) {
  const fileRef = useRef(null);

  const set = (patch) => onChange(index, patch);

  const onFile = (f) => {
    if (!f) return;
    if (product.imagePreviewUrl) URL.revokeObjectURL(product.imagePreviewUrl);
    set({
      imageFile: f,
      imagePreviewUrl: URL.createObjectURL(f),
      imageUrl: "",
    });
  };

  const clearFile = () => {
    if (product.imagePreviewUrl) URL.revokeObjectURL(product.imagePreviewUrl);
    set({ imageFile: null, imagePreviewUrl: "", imageUrl: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const previewSrc = product.imagePreviewUrl || product.imageUrl || null;

  return (
    <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      {/* Kart numarası + sil */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
          Ürün {index + 1}
        </span>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-slate-300 hover:text-red-500 transition"
          title="Ürünü kaldır"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Görsel */}
      <div className="flex gap-3 items-start">
        {/* Önizleme kutusu */}
        <div className="shrink-0 w-20 h-20 rounded-lg border-2 border-dashed border-slate-200 bg-white overflow-hidden flex items-center justify-center relative group cursor-pointer hover:border-slate-400 transition">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="önizleme"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <Package className="w-7 h-7 text-slate-300 group-hover:text-slate-400 transition" />
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </div>

        {/* URL veya dosya adı */}
        <div className="flex-1 space-y-1.5">
          {product.imageFile ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600 truncate max-w-[160px]">
                {product.imageFile.name}
              </span>
              <button
                type="button"
                onClick={clearFile}
                className="text-xs text-red-400 hover:text-red-600 transition shrink-0"
              >
                Kaldır
              </button>
            </div>
          ) : (
            <input
              type="text"
              value={product.imageUrl}
              onChange={(e) => set({ imageUrl: e.target.value })}
              placeholder="Görsel URL (https://…)"
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800
                focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
                placeholder:text-slate-400 transition bg-white"
            />
          )}
          <p className="text-[10px] text-slate-400">
            URL girin veya sol kutuya tıklayıp dosya seçin
          </p>
        </div>
      </div>

      {/* Ürün adı */}
      <input
        type="text"
        value={product.ad}
        onChange={(e) => set({ ad: e.target.value })}
        placeholder="Ürün / Hizmet adı"
        maxLength={120}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
          focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
          placeholder:text-slate-400 transition bg-white"
      />

      {/* Açıklama */}
      <textarea
        value={product.aciklama}
        onChange={(e) => set({ aciklama: e.target.value })}
        placeholder="Kısa açıklama (opsiyonel)"
        rows={2}
        maxLength={500}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
          focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
          placeholder:text-slate-400 transition resize-none bg-white"
      />
    </div>
  );
}

/* ── Ana bileşen ── */
export default function ProductsSection({ products, setProducts }) {
  const addProduct = () => {
    if (products.length >= MAX_PRODUCTS) return;
    setProducts((prev) => [...prev, emptyProduct()]);
  };

  const updateProduct = (index, patch) => {
    setProducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p))
    );
  };

  const removeProduct = (index) => {
    setProducts((prev) => {
      const p = prev[index];
      if (p.imagePreviewUrl) URL.revokeObjectURL(p.imagePreviewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="space-y-3">
      {products.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-8 flex flex-col items-center gap-2 text-slate-400">
          <Package className="w-8 h-8" />
          <p className="text-sm">Henüz ürün eklenmedi</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {products.map((p, i) => (
            <ProductCard
              key={p._key}
              product={p}
              index={i}
              onChange={updateProduct}
              onRemove={removeProduct}
            />
          ))}
        </div>
      )}

      {products.length < MAX_PRODUCTS && (
        <button
          type="button"
          onClick={addProduct}
          className={cx(
            "w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-2.5 text-sm font-medium transition",
            products.length === 0
              ? "border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700"
              : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
          )}
        >
          <Plus className="w-4 h-4" />
          Ürün Ekle
          <span className="text-xs text-slate-300 ml-1">
            ({products.length}/{MAX_PRODUCTS})
          </span>
        </button>
      )}
    </div>
  );
}